import { Component, Vue } from 'vue-property-decorator';
import { startCase, toLower } from 'lodash';
import { tableHeight } from '@/helpers';
import { CONTRIBUTION_LEVELS } from '@/constants';
import { SelectionOptions, TableHeaderColumns, UserContributionLevelDTO } from '@/models';
import { fetchContributionLevels } from '@/services';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import ExportByDrop from '@/services/export-by-drop';

@Component({
  components: {
    FilterDropdown,
    SearchInput,
    UserInformation,
    Pagination,
    InlineLoader
  }
})
export default class DropStatistics extends Vue {
  private levelOptions: SelectionOptions = [];
  private searchUser = '';
  private selectedLevels = [] as string[];
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  private userDrops: UserContributionLevelDTO[] = [];
  private selectedRows: UserContributionLevelDTO[] = [];
  private isFetchingUserDrops = true;

  get tableHeight(): string {
    return tableHeight(this.pageCount);
  }

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      {
        text: '#',
        value: 'index'
      },
      {
        text: 'User',
        value: 'user'
      },
      {
        text: 'Level',
        value: 'currentLevel',
        class: 'grey--text'
      },
      {
        text: 'Redeemable Pollens',
        value: 'redeemableDrops',
        class: 'grey--text '
      },
      {
        text: 'Total Pollens',
        value: 'accumulatedDrops',
        class: 'grey--text'
      }
    ];
  }

  filterByUser<T>(value: keyof T, search: string, item: UserContributionLevelDTO): boolean {
    return (
      value !== null &&
      search !== null &&
      (item.user.fullName.toLowerCase().includes(search.toLowerCase()) || item.user.code.toString().includes(search))
    );
  }

  async exportUserDrops(): Promise<void> {
    const userToExport = this.selectedRows.length ? this.selectedRows : this.userDrops;
    const exportByDrop = new ExportByDrop(userToExport);
    await exportByDrop.generateXLSXFile();
  }

  onSearch(searchText: string): void {
    this.searchUser = searchText;
  }

  onLevelChange(selectedLevels: string[]): void {
    this.selectedLevels = selectedLevels;
  }

  get displayUserDrops(): UserContributionLevelDTO[] {
    const { userDrops, selectedLevels } = this;
    let result = userDrops.map((userDrop, idx) => {
      return {
        ...userDrop,
        index: idx + 1
      };
    });

    if (selectedLevels.length) {
      result = result.filter(userDrop => {
        return selectedLevels.includes('' + userDrop.currentLevel);
      });
    }
    return result;
  }

  getContributionLevelNameFromText(contributionLevelText: string): string {
    return CONTRIBUTION_LEVELS[contributionLevelText].name;
  }

  async created(): Promise<void> {
    const { data: allMembersData } = await fetchContributionLevels();
    const userDrops = allMembersData.map(memberData => {
      return {
        ...memberData,
        redeemableDrops: memberData.accumulatedDrops - (memberData?.redeemedDrops || 0)
      };
    });
    userDrops.sort((a, b) => {
      return b.redeemableDrops - a.redeemableDrops;
    });
    this.userDrops = userDrops;

    Object.keys(CONTRIBUTION_LEVELS).forEach(item => {
      this.levelOptions.push({ text: startCase(toLower(item)), value: item });
    });
    this.isFetchingUserDrops = false;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  get textExportButton(): string {
    if (this.selectedRows.length === this.userDrops.length || this.selectedRows.length === 0) {
      return 'Export all';
    }
    return this.selectedRows.length !== 1 ? `Export ${this.selectedRows.length} records` : 'Export 1 record';
  }
}
